/**
 * Entity graph pop-over (sites#249): Candidate and Committee pages by FEC id;
 * Individual, voter and geography pages (State, District, County, Place,
 * PostalCode, Tract, Block, Precinct) since the graph-views follow-up.
 * us-pages marks a geography page with data-graph-label/-id on this script's
 * tag; the client-rendered leaf pages queue a target once their detail has
 * rendered (graph-target.ts).
 *
 * us-pages writes this script next to money-flow.js. Where the site has
 * accounts (Authentik on), "Graph" is a member control in the identity bar
 * (sites#355): every visitor sees it, signed-in visitors can open it. Where it
 * has none (CI pods, local runs), it stays a "Graph" button in the page header.
 * Opening it fetches `entityGraph` from api-graphql and loads the renderer
 * bundle (/js/entity-graph-view.js), then draws the node and its most
 * significant neighbours in a modal dialog. Nothing on the page changes until
 * it is used, and on any failure the dialog says so instead of drawing.
 */

import { canonicalPath } from '@electinfo/common';
import { currentCycle } from './cycle-facet';
import { GRAPH_ICON } from './graph-control';
import { escapeHtml, readFecId } from './entity-page';
import { expandTarget } from './entity-graph-expand';
import type { EntityGraph, GraphNode, GraphTarget, GraphView } from './entity-graph-types';

const QUERY = `query EntityGraph($fecId: String!) {
  entityGraph(fecId: $fecId) {
    fecId focalId
    nodes { id label labels displayName fecId party affinityDem affinityRep focal }
    edges { id source target type amount count }
    groups { type direction label shown total }
  }
}`;

// With a cycle selected (the cycle facet, sites#194) the money edges are that
// cycle's; other connections are not filtered. A separate fixed document, so the
// all-cycles request stays exactly as before.
const CYCLE_QUERY = `query EntityGraphCycle($fecId: String!, $cycle: Int!) {
  entityGraph(fecId: $fecId, cycle: $cycle) {
    fecId focalId
    nodes { id label labels displayName fecId party affinityDem affinityRep focal }
    edges { id source target type amount count }
    groups { type direction label shown total }
  }
}`;

// Individual and geography pages (nodeGraph). The cycle filters an
// Individual's contributions; geographies are the same in every cycle.
const NODE_QUERY = `query NodeGraph($label: String!, $id: String!) {
  nodeGraph(label: $label, id: $id) {
    fecId focalId
    nodes { id label labels displayName fecId party affinityDem affinityRep focal }
    edges { id source target type amount count }
    groups { type direction label shown total }
  }
}`;

const NODE_CYCLE_QUERY = `query NodeGraphCycle($label: String!, $id: String!, $cycle: Int!) {
  nodeGraph(label: $label, id: $id, cycle: $cycle) {
    fecId focalId
    nodes { id label labels displayName fecId party affinityDem affinityRep focal }
    edges { id source target type amount count }
    groups { type direction label shown total }
  }
}`;

// Voters are premium and not in the graph: voterGraph, on the voters endpoint
// (voter.ts VOTERS_GRAPHQL_ENDPOINT; not imported, that module runs the voters page).
const VOTER_QUERY = `query VoterGraph($id: String!) {
  voterGraph(id: $id) {
    fecId focalId
    nodes { id label labels displayName fecId party affinityDem affinityRep focal }
    edges { id source target type amount count }
    groups { type direction label shown total }
  }
}`;
const VOTERS_ENDPOINT = '/voters/api/graphql';

const VIEW_SRC = '/js/entity-graph-view.js';

/** Graph label → @electinfo/common entity type for canonicalPath; others are not linked. */
const PAGE_TYPES: Record<string, string> = {
  Committee: 'Committee',
  Candidate: 'Candidate',
  Politician: 'Politician',
  Party: 'Party',
  State: 'State',
  District: 'District',
  Office: 'Office',
  Cycle: 'Cycle',
  County: 'County',
  Place: 'Place',
  PostalCode: 'PostalCode',
  Vendor: 'Vendor',
  Employer: 'Employer',
  Individual: 'Individual',
  Tract: 'Tract',
  Block: 'Block',
  Precinct: 'Precinct',
};

/** Leaf collections by label, where canonicalPath does not know the type. */
const LEAF_COLLECTIONS: Record<string, string> = {
  Individual: 'individuals',
  Tract: 'tracts',
  Block: 'blocks',
  Precinct: 'precincts',
};

export function nodeUrl(n: GraphNode): string | null {
  const type = PAGE_TYPES[n.label];
  if (!type || n.focal) return null;
  try {
    return `/${canonicalPath(type, n.id)}/`;
  } catch {
    const collection = LEAF_COLLECTIONS[n.label];
    return collection ? `/${collection}/${encodeURIComponent(n.id.toLowerCase())}/` : null;
  }
}

/**
 * "TRANSFERS_TO in from Committee: 20 of 2,261" lines for groups that were cut;
 * a group drawn not at all (blocks, resident donors) is a count.
 */
export function truncationNotes(graph: EntityGraph): string[] {
  return graph.groups
    .filter((g) => g.total > g.shown)
    .map((g) => {
      const dir = g.direction === 'in' ? 'from' : 'to';
      const total = g.total.toLocaleString('en-US');
      if (g.shown === 0) return `${g.type} ${dir} ${g.label}: ${total} (counted, not drawn)`;
      return `${g.type} ${dir} ${g.label}: top ${g.shown.toLocaleString('en-US')} of ${total}`;
    });
}

/** The money cycle a target's graph is filtered by: candidates, committees and individuals only. */
export function graphCycle(target: GraphTarget, cycle: number | null): number | null {
  if (target.kind === 'fec' || (target.kind === 'node' && target.label === 'Individual')) return cycle;
  return null;
}

class PremiumGraphError extends Error {}

function graphRequest(target: GraphTarget, cycle: number | null): { endpoint: string; field: string; body: object } {
  if (target.kind === 'voter') {
    return { endpoint: VOTERS_ENDPOINT, field: 'voterGraph', body: { query: VOTER_QUERY, variables: { id: target.id } } };
  }
  if (target.kind === 'node') {
    const variables = { label: target.label, id: target.id };
    return {
      endpoint: '/api/graphql',
      field: 'nodeGraph',
      body: cycle ? { query: NODE_CYCLE_QUERY, variables: { ...variables, cycle } } : { query: NODE_QUERY, variables },
    };
  }
  const fecId = target.fecId;
  return {
    endpoint: '/api/graphql',
    field: 'entityGraph',
    body: cycle ? { query: CYCLE_QUERY, variables: { fecId, cycle } } : { query: QUERY, variables: { fecId } },
  };
}

async function fetchGraph(target: GraphTarget, cycle: number | null): Promise<EntityGraph | null> {
  const { endpoint, field, body } = graphRequest(target, cycle);
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 401 || res.status === 403) throw new PremiumGraphError();
  if (!res.ok) return null;
  const json = await res.json();
  if ((json?.errors as { extensions?: { code?: string } }[] | undefined)?.some((e) => e.extensions?.code === 'FORBIDDEN')) {
    throw new PremiumGraphError();
  }
  return (json?.data?.[field] as EntityGraph) ?? null;
}

let viewScript: Promise<void> | null = null;

function loadView(doc: Document): Promise<void> {
  if (window.electinfoGraphView) return Promise.resolve();
  if (!viewScript) {
    viewScript = new Promise<void>((resolve, reject) => {
      const s = doc.createElement('script');
      s.src = VIEW_SRC;
      s.async = true;
      s.onload = () => (window.electinfoGraphView ? resolve() : reject(new Error('graph view did not register')));
      s.onerror = () => reject(new Error(`failed to load ${VIEW_SRC}`));
      doc.head.appendChild(s);
    }).catch((err) => {
      viewScript = null; // allow a retry on the next open
      throw err;
    });
  }
  return viewScript;
}

function injectStyles(doc: Document): void {
  if (doc.getElementById('entity-graph-style')) return;
  const style = doc.createElement('style');
  style.id = 'entity-graph-style';
  // The entity pages' stylesheet zeroes every element's margin, which undoes
  // the browser's centring of a modal <dialog>: position, inset and
  // margin:auto restore it, horizontally and vertically.
  style.textContent = `
.eg-open{display:inline-flex;align-items:center;gap:.4rem;margin:.25rem 0;padding:4px 10px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#1e293b;font:inherit;font-size:.875rem;cursor:pointer}
.eg-open:hover{background:#f1f5f9}
.eg-open:focus-visible{outline:2px solid #2563eb;outline-offset:2px}
dialog.eg-dialog{position:fixed;inset:0;margin:auto;width:min(1200px,96vw);height:min(860px,92vh);max-width:96vw;max-height:92vh;padding:0;border:1px solid #cbd5e1;border-radius:10px;box-shadow:0 20px 50px rgba(15,23,42,.35)}
dialog.eg-dialog::backdrop{background:rgba(15,23,42,.45)}
.eg-frame{display:flex;flex-direction:column;height:100%}
.eg-bar{display:flex;align-items:center;gap:.75rem;padding:.6rem .9rem;border-bottom:1px solid #e2e8f0}
.eg-bar h2{flex:1 1 auto;margin:0;font-size:1rem}
.eg-bar button{border:1px solid #cbd5e1;border-radius:6px;background:#fff;padding:3px 9px;cursor:pointer;font:inherit;font-size:.85rem}
.eg-canvas{position:relative;flex:1 1 auto;min-height:0;background:#f8fafc}
.eg-status{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#475569;font-size:.95rem}
.eg-foot{padding:.5rem .9rem;border-top:1px solid #e2e8f0;font-size:.78rem;color:#475569;max-height:5.5rem;overflow:auto}
.eg-legend{display:flex;flex-wrap:wrap;gap:.35rem .9rem;margin-bottom:.25rem}
.eg-legend span{display:inline-flex;align-items:center;gap:.3rem}
.eg-legend i{display:inline-block;width:10px;height:10px;border-radius:50%}`;
  doc.head.appendChild(style);
}

/** Legend entries the view uses; kept here so the footer renders before the view loads. */
const LEGEND: [string, string][] = [
  ['Committee', '#64748b'],
  ['Candidate', '#0f766e'],
  ['Vendor', '#d97706'],
  ['Party', '#7c3aed'],
  ['Geography', '#0891b2'],
  ['Office', '#4f46e5'],
  ['Employer', '#b45309'],
  ['Donor / voter', '#be185d'],
  ['Cycle', '#a3a3a3'],
];

function buildDialog(doc: Document, title: string, cycle: number | null): {
  dialog: HTMLDialogElement;
  canvas: HTMLElement;
  status: HTMLElement;
  foot: HTMLElement;
  fitBtn: HTMLButtonElement;
} {
  const dialog = doc.createElement('dialog');
  dialog.className = 'eg-dialog';
  dialog.setAttribute('aria-label', `${title}: graph of connected entities`);
  dialog.innerHTML = `
<div class="eg-frame">
  <div class="eg-bar">
    <h2>${escapeHtml(title)} <span style="font-weight:400;color:#64748b">— connected entities${cycle ? `, ${cycle - 1}–${cycle} money` : ''}</span></h2>
    <button type="button" data-eg="fit">Fit</button>
    <button type="button" data-eg="close" aria-label="Close graph">Close</button>
  </div>
  <div class="eg-canvas" role="img" aria-label="Network graph of ${escapeHtml(title)} and its most significant connections">
    <div class="eg-status">Loading graph…</div>
  </div>
  <div class="eg-foot">
    <div class="eg-legend">${LEGEND.map(([l, c]) => `<span><i style="background:${c}"></i>${l}</span>`).join('')}
      <span>Committees and candidates are tinted by party</span></div>
    <div data-eg="notes"></div>
  </div>
</div>`;
  doc.body.appendChild(dialog);
  dialog.querySelector('[data-eg="close"]')!.addEventListener('click', () => dialog.close());
  // Click on the backdrop (outside the frame) closes too.
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) dialog.close();
  });
  return {
    dialog,
    canvas: dialog.querySelector<HTMLElement>('.eg-canvas')!,
    status: dialog.querySelector<HTMLElement>('.eg-status')!,
    foot: dialog.querySelector<HTMLElement>('[data-eg="notes"]')!,
    fitBtn: dialog.querySelector<HTMLButtonElement>('[data-eg="fit"]')!,
  };
}

type GraphHost = {
  __electinfoEntityGraph?: boolean;
  __electinfoGraphTarget?: boolean;
  electinfoAuth?: Promise<unknown | null>;
  electinfoBarControls?: { push: (...c: object[]) => void } | object[];
};

/** The page's own target: an FEC id (candidate/committee) or this script tag's data-graph-label/-id (us-pages). */
export function pageTarget(doc: Document): GraphTarget | null {
  const title = doc.querySelector('header h1')?.textContent?.trim() || '';
  const fecId = readFecId(doc);
  if (fecId && doc.querySelector('header p.fec-id, p.fec-id')) return { kind: 'fec', fecId, title: title || fecId };
  const tag = doc.querySelector<HTMLScriptElement>('script[src*="/js/entity-graph.js"][data-graph-id]');
  const label = tag?.dataset.graphLabel;
  const id = tag?.dataset.graphId;
  if (label && id) return { kind: 'node', label, id, title: title || label };
  return null;
}

async function init(): Promise<void> {
  const w = window as unknown as GraphHost;
  if (w.__electinfoEntityGraph) return; // nginx injection + a future builder tag
  w.__electinfoEntityGraph = true;

  const own = pageTarget(document);
  if (own) await attach(w, own);
  // Leaf pages queue a target after rendering (graph-target.ts), before or after this runs.
  const queued = Array.isArray(window.electinfoGraphTargets) ? window.electinfoGraphTargets : [];
  window.electinfoGraphTargets = { push: (...targets) => targets.forEach((t) => void attach(w, t)) };
  for (const t of queued) await attach(w, t);
}

/** Register the Graph control (accounts) or a header button (no accounts) for one target, once per page. */
async function attach(w: GraphHost, target: GraphTarget): Promise<void> {
  if (w.__electinfoGraphTarget) return;
  w.__electinfoGraphTarget = true;
  injectStyles(document);

  const open = graphDialog(target);
  // auth.js resolves electinfoAuth to null where Authentik is off: no accounts, no gate.
  const accounts = (await authState(w)) != null;
  if (accounts) {
    const queue = (w.electinfoBarControls = w.electinfoBarControls || []) as { push: (...c: object[]) => void };
    queue.push({ id: 'graph', label: 'Graph', requires: 'graph', title: 'Show connected entities as a graph', icon: GRAPH_ICON, onActivate: open });
    return;
  }
  const anchor = document.querySelector<HTMLElement>('header p.fec-id, p.fec-id, header p.subtitle, header h1');
  if (!anchor) return;
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'eg-open';
  button.innerHTML = `${GRAPH_ICON}<span>Graph</span>`;
  button.title = 'Show connected entities as a graph';
  anchor.insertAdjacentElement('afterend', button);
  button.addEventListener('click', open);
}

/** The footer's account of what is drawn, per kind of graph. */
export function scopeNote(target: GraphTarget, cycle: number | null): string {
  if (target.kind === 'voter') {
    return 'The voter\'s state, county, congressional district, ZIP and city in the graph, and the areas containing them. ' +
      'State legislative districts and precincts are not graph entities. ';
  }
  if (target.kind === 'node' && target.label === 'Individual') {
    return (cycle ? `Contributions are the ${cycle - 1}–${cycle} cycle only. ` : '') +
      'Top 20 committees by amount given; employer and residence, with the areas containing it. ';
  }
  if (target.kind === 'node') {
    return 'Most significant neighbours: top 10 per relationship by size (receipts, population, voters), ' +
      'and the counties, districts and states containing each area. Census blocks and individual donors are counted, not drawn. ';
  }
  return (cycle
    ? `Money edges are the ${cycle - 1}–${cycle} cycle only; other connections (party, state, office, cycles) are not filtered. `
    : '') +
    'Most significant neighbours: top 20 per money relationship by amount, top 10 otherwise; individual donors are not shown. ';
}

/**
 * What auth.js's window.electinfoAuth resolves to: the visitor, null where
 * Authentik is off, undefined without auth.js. Both scripts are deferred and
 * this one comes first on us-pages, so wait for DOMContentLoaded (or load, if
 * that has passed) when auth.js has not run yet.
 */
async function authState(w: GraphHost): Promise<unknown> {
  if (!w.electinfoAuth && document.readyState !== 'complete') {
    await new Promise<void>((resolve) => {
      document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
      window.addEventListener('load', () => resolve(), { once: true });
    });
  }
  return w.electinfoAuth ? w.electinfoAuth.catch(() => null) : undefined;
}

/** The graph dialog for one entity: open() shows it, drawing on first use. */
function graphDialog(target: GraphTarget): () => void {
  const cycle = graphCycle(target, currentCycle());
  const title = target.title;

  let ui: ReturnType<typeof buildDialog> | null = null;
  let view: GraphView | null = null;
  let loading: Promise<void> | null = null;

  const draw = async (): Promise<void> => {
    const [graph] = await Promise.all([fetchGraph(target, cycle), loadView(document)]);
    if (!ui) return;
    if (!graph || graph.nodes.length < 2) {
      ui.status.textContent = 'No connected entities to show.';
      return;
    }
    ui.status.remove();
    const expandedNames: string[] = [];
    const writeFoot = (): void => {
      if (!ui) return;
      const notes = truncationNotes(graph);
      ui.foot.innerHTML =
        escapeHtml(scopeNote(target, cycle)) +
        'Edge width grows with the amount (log scale). Hover a node or zoom in to read edge types; ' +
        'click a node to expand its connections or open its page.' +
        (notes.length ? `<br>${notes.map(escapeHtml).join(' · ')}` : '') +
        (expandedNames.length ? `<br>Expanded: ${expandedNames.map(escapeHtml).join(' · ')}` : '');
    };
    const mounted = (view = window.electinfoGraphView!.mount(ui.canvas, graph, {
      onOpen: (n) => {
        const url = nodeUrl(n);
        if (url) window.location.href = url;
      },
      canOpen: (n) => nodeUrl(n) != null,
      canExpand: (n) => expandTarget(n) != null,
      onExpand: async (n) => {
        const next = expandTarget(n);
        if (!next) return null;
        let expansion: EntityGraph | null;
        try {
          expansion = await fetchGraph(next, graphCycle(next, currentCycle()));
        } catch (err) {
          if (err instanceof PremiumGraphError) throw new Error('These connections need a premium elect.info account');
          throw new Error('The connections could not be loaded');
        }
        if (expansion) {
          expandedNames.push(n.displayName);
          writeFoot();
        }
        return expansion;
      },
    }));
    // The e2e tests click nodes through it (renderedPosition).
    (ui.dialog as HTMLDialogElement & { electinfoGraph?: GraphView }).electinfoGraph = mounted;
    writeFoot();
  };

  return () => {
    if (!ui) {
      ui = buildDialog(document, title, cycle);
      ui.fitBtn.addEventListener('click', () => view?.fit());
    }
    ui.dialog.showModal();
    if (view) {
      view.fit();
      return;
    }
    if (!loading) {
      loading = draw().catch((err) => {
        console.warn('entity-graph: not rendered', err);
        if (ui) {
          const status = ui.canvas.querySelector('.eg-status') ?? ui.canvas.appendChild(document.createElement('div'));
          status.className = 'eg-status';
          status.textContent = err instanceof PremiumGraphError
            ? 'This graph needs a premium elect.info account.'
            : 'The graph could not be loaded. Please try again later.';
        }
        loading = null; // retry on the next open
      });
    }
  };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void init());
} else {
  void init();
}
